import React, { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import OverlayLoading from "../shared/components/OverlayLoading";
import Title from "../shared/components/Title";
import DropzoneComponent from "../shared/components/DropzoneComponent";
import html2canvas from "html2canvas";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFile, faFileCsv, faUpload } from "@fortawesome/free-solid-svg-icons";
import TotalEnquiries from "../features/customer-care-kpi-performance/components/TotalEnquiries";
import AverageTimeToClose from "../features/customer-care-kpi-performance/components/AverageTimeToClose";
import ActivityByMonth from "../features/customer-care-kpi-performance/components/ActivityByMonth";
import TicketsByVertical from "../features/customer-care-kpi-performance/components/TicketByVertical";
import AvgTimeToCloseByVerticalMonth from "../features/customer-care-kpi-performance/components/AvgTimeToCloseByVerticalMonth";
import apiClient from "../utils/apiClient";
import ActivityProactiveRatio from "../features/customer-care-kpi-performance/components/AcitvityProactiveRatio";
import CustomerCarePerformance from "../features/customer-care-kpi-performance/components/CustomerCarePerformance";
import { colors } from "../global";
import SatisfactionRating from "../features/customer-care-kpi-performance/components/SatisfactionRating";
import VerticalMetrics from "../features/customer-care-kpi-performance/components/VerticalMetrics";

const CHART_KEYS = [
  "total_enquiries",
  "average_time_to_close_monthly",
  "activity_chart",
  "tickets_by_vertical",
  "avg_time_by_vertical_month",
  "activity_and_proactive_ratio_kpi",
  "avg_response_time",
  "total_calls",
  "satisfaction_rating",
  "vertical_metrics",
];

const toCamelCase = (str) =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const CustomerCareKPIPerformance = () => {
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dashboardData, setDashboardData] = useState({});

  const chartRefs = useRef(
    CHART_KEYS.reduce((acc, key) => {
      acc[toCamelCase(key)] = React.createRef();
      return acc;
    }, {})
  ).current;

  const onDrop = useCallback((acceptedFiles) => {
    setFiles(acceptedFiles);
  }, []);
  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("No files selected");
      return;
    }
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      files.forEach((file, idx) => {
        formData.append("files", file);
      });

      const response = await apiClient.post(
        `/api/v1/customerCareKPIPerformance/upload`,
        formData
      );

      if (!response.ok) {
        let errorMessage = "Upload failed.";
        try {
          const errorResponse = await response.json();
          errorMessage =
            errorResponse.detail || errorResponse.error || errorMessage;
        } catch (err) {
          console.error("Failed to parse error response", err);
        }
        toast.error(errorMessage);
        return;
      }

      const result = await response.json();
      setDashboardData(result);
      toast.success("Upload successful");
      setFiles([]);
    } catch (err) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: true,
  });

  const captureChart = async (ref) => {
    if (!ref?.current) return null;
    const canvas = await html2canvas(ref.current, {
      useCORS: true,
      scale: 2,
    });
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  };

  const handleExportAllChartsToAPI = async () => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      for (const key of CHART_KEYS) {
        const blob = await captureChart(chartRefs[toCamelCase(key)]);
        if (blob) {
          formData.append("images", blob, `${key}.png`);
        }
      }

      const response = await apiClient.post(
        "/api/v1/customerCareKPIPerformance/generate",
        formData
      );

      if (!response.ok) {
        throw new Error("Failed to generate PowerPoint");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Slide_Presentation_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.pptx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "csv":
        return <FontAwesomeIcon icon={faFileCsv} />;
      default:
        return <FontAwesomeIcon icon={faFile} />;
    }
  };

  return (
    <div>
      {isSubmitting && (
        <OverlayLoading
          isVisible={isSubmitting}
          message="Processing your request..."
          icon="truck"
        />
      )}

      <div className="p-6 pb-0">
        <Title title={"Customer Care KPI Performance"} />
      </div>

      <div className="m-4 p-6 bg-white rounded-xl border border-gray-100 shadow-sm">
        <DropzoneComponent
          getInputProps={getInputProps}
          getRootProps={getRootProps}
          isDragActive={isDragActive}
          fileTypes="CSV"
        />

        {files.length > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Selected Files ({files.length})
              </h3>
              <div className="text-sm text-gray-500">
                Total:{" "}
                {formatFileSize(
                  files.reduce((acc, file) => acc + file.size, 0)
                )}
              </div>
            </div>
            <ul className="list-disc pl-5">
              {files.map((file, index) => (
                <div
                  key={file.name + index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors mt-3"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {getFileIcon(file.name)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(file.size)} • CSV File
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </ul>

            <button
              onClick={handleUpload}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              disabled={isSubmitting}
            >
              <FontAwesomeIcon icon={faUpload} className="mr-2" /> Upload All
            </button>
          </div>
        )}
      </div>
      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {dashboardData?.avg_response_time_kpi_result &&
          dashboardData?.closed_within_24_kpi_result &&
          dashboardData?.closed_within_48_kpi_result && (
            <div className="col-span-2">
              <div
                className="bg-white p-4 rounded-lg shadow"
                ref={chartRefs.avgResponseTime}
              >
                <CustomerCarePerformance
                  data={{
                    avg_response_time:
                      dashboardData.avg_response_time_kpi_result,
                    closed_24: dashboardData.closed_within_24_kpi_result,
                    closed_48: dashboardData.closed_within_48_kpi_result,
                  }}
                />
              </div>
            </div>
          )}
        {dashboardData?.avg_response_time_kpi_result &&
          dashboardData?.closed_within_24_kpi_result &&
          dashboardData?.closed_within_48_kpi_result && (
            <>
              <div
                className="bg-white p-4 rounded-lg shadow"
                ref={chartRefs.satisfactionRating}
              >
                <SatisfactionRating />
              </div>
              <div className=""></div>
            </>
          )}
        {dashboardData?.tickets_by_vertical?.series?.length > 0 && (
          <div className="col-span-2" ref={chartRefs.verticalMetrics}>
            <VerticalMetrics />
          </div>
        )}
        {dashboardData?.total_enquiries?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.totalEnquiries}
          >
            <TotalEnquiries data={dashboardData.total_enquiries} />
          </div>
        )}
        {dashboardData?.average_time_to_close_monthly?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.averageTimeToCloseMonthly}
          >
            <AverageTimeToClose
              data={dashboardData.average_time_to_close_monthly}
            />
          </div>
        )}
        {dashboardData?.tickets_by_vertical?.series?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.ticketsByVertical}
          >
            <TicketsByVertical data={dashboardData.tickets_by_vertical} />
          </div>
        )}
        {dashboardData?.avg_time_by_vertical_month?.series?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.avgTimeByVerticalMonth}
          >
            <AvgTimeToCloseByVerticalMonth
              data={dashboardData.avg_time_by_vertical_month}
            />
          </div>
        )}
        {dashboardData?.activity_chart?.series?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.totalCalls}
          >
            <ActivityByMonth data={dashboardData.activity_chart} />
          </div>
        )}
        {dashboardData?.activity_chart?.series?.length > 0 && (
          <div
            className="bg-white p-4 rounded-lg shadow"
            ref={chartRefs.activityChart}
          >
            <ActivityByMonth data={dashboardData.activity_chart} />
          </div>
        )}

        {dashboardData?.activity_ratio_kpi_result &&
          dashboardData?.proactive_activity_ratio_kpi_result && (
            <div
              className="bg-white p-4 rounded-lg shadow"
              ref={chartRefs.activityAndProactiveRatioKpi}
            >
              <ActivityProactiveRatio
                activityRatio={dashboardData.activity_ratio_kpi_result}
                proactiveRatio={
                  dashboardData.proactive_activity_ratio_kpi_result
                }
              />
            </div>
          )}
        <div className="col-span-1"></div>
      </div>
      {Object.keys(dashboardData).length > 0 && (
        <button
          onClick={handleExportAllChartsToAPI}
          className="px-4 py-2 rounded-lg flex font-medium transition-colors disabled:opacity-50"
          style={{
            backgroundColor: colors.success,
            color: colors.white,
          }}
        >
          Export PowerPoint
        </button>
      )}
    </div>
  );
};

export default CustomerCareKPIPerformance;
